// The frontend's provider registry for cloud accounts (spec 6.6): order, tiles, and the authentication methods
// ordered per provider from most to least recommended (design 10, P1.12).
import type { CloudAccount } from '../api/types';
import { strings } from '../strings';

const s = strings.cloudAccounts;

export type MethodBadge = 'recommended' | 'no-secrets' | 'development' | 'discouraged';

export interface MethodEntry {
  key: string;
  label: string;
  description: string;
  badge: MethodBadge | null;
}

export interface ProviderEntry {
  /** Short text of the provider tile. */
  tile: string;
  /** Fallback when `GET /providers` doesn't name it. */
  displayName: string;
  methods: MethodEntry[];
}

const m = s.methods;

export const PROVIDERS: Record<string, ProviderEntry> = {
  azure: {
    tile: 'AZ',
    displayName: 'Microsoft Azure',
    methods: [
      { key: 'managed-identity', ...m.azureManagedIdentity, badge: 'recommended' },
      { key: 'federated', ...m.azureFederated, badge: 'no-secrets' },
      { key: 'ambient', ...m.azureAmbient, badge: 'development' },
      { key: 'client-secret', ...m.azureClientSecret, badge: 'discouraged' },
    ],
  },
  aws: {
    tile: 'AWS',
    displayName: 'Amazon Web Services',
    methods: [
      { key: 'assume-role', ...m.awsAssumeRole, badge: 'recommended' },
      { key: 'federated', ...m.awsFederated, badge: 'no-secrets' },
      { key: 'ambient', ...m.awsAmbient, badge: null },
      { key: 'access-key', ...m.awsAccessKey, badge: 'discouraged' },
    ],
  },
  gcp: {
    tile: 'GCP',
    displayName: 'Google Cloud',
    methods: [
      { key: 'impersonate', ...m.gcpImpersonate, badge: 'recommended' },
      { key: 'federated', ...m.gcpFederated, badge: 'no-secrets' },
      { key: 'ambient', ...m.gcpAmbient, badge: null },
      { key: 'service-account-key', ...m.gcpServiceAccountKey, badge: 'discouraged' },
    ],
  },
};

/** Providers in display order: Azure, AWS, GCP, then others by key. */
export function providerOrder(keys: Iterable<string>): string[] {
  const known = ['azure', 'aws', 'gcp'];
  const all = [...new Set(keys)];
  return [...known.filter((k) => all.includes(k)), ...all.filter((k) => !known.includes(k)).sort()];
}

export function methodsOf(provider: string): MethodEntry[] {
  return PROVIDERS[provider]?.methods ?? [];
}

export function methodLabel(provider: string, method: string): string {
  return methodsOf(provider).find((x) => x.key === method)?.label ?? method;
}

export function isDiscouraged(provider: string, method: string): boolean {
  return methodsOf(provider).find((x) => x.key === method)?.badge === 'discouraged';
}

/** The scope in a few characters: Azure "sub …0002", AWS the account ID, GCP the project ID. */
export function shortScope(account: Pick<CloudAccount, 'provider' | 'scope'>): string {
  switch (account.provider) {
    case 'azure': {
      const subscription = account.scope.subscriptionId ?? '';
      return subscription ? s.shortSubscription(subscription.slice(-4)) : '';
    }
    case 'aws':
      return account.scope.accountId ?? '';
    case 'gcp':
      return account.scope.projectId ?? '';
    default:
      return Object.values(account.scope)[0] ?? '';
  }
}

export function providerTile(provider: string): string {
  return PROVIDERS[provider]?.tile ?? provider.slice(0, 3).toUpperCase();
}
